const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

async function authFetch<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error?.message || `Auth error: ${res.status}`);
  }
  const data = await res.json();
  return data.data ?? data;
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: { id: string; email: string; name: string; role: 'admin' | 'issuer' | 'verifier' | 'holder' };
}

// The register/login responses carry a `devOtp` field only when the API runs in
// dev mode (no Resend key). It is empty/absent in production.
interface OTPSendResponse {
  message?: string;
  devOtp?: string;
}

export async function apiRegister(email: string, name: string): Promise<string | undefined> {
  const resp = await authFetch<OTPSendResponse>('/auth/register', { email, name });
  return resp?.devOtp;
}

export async function apiLogin(email: string): Promise<string | undefined> {
  const resp = await authFetch<OTPSendResponse>('/auth/login', { email });
  return resp?.devOtp;
}

export async function apiVerifyOTP(email: string, code: string): Promise<AuthResponse> {
  return authFetch<AuthResponse>('/auth/verify-otp', { email, code });
}

export async function apiRefresh(refreshToken: string): Promise<AuthResponse> {
  return authFetch<AuthResponse>('/auth/refresh', { refreshToken });
}

export async function apiGetMe(token: string): Promise<AuthResponse['user']> {
  const res = await fetch(`${API_BASE}/auth/me`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Session expired');
  const data = await res.json();
  return data.data ?? data;
}

export interface WalletInfo {
  id: string;
  address: string;
  publicKey: string;
  keyType: string;
  chainId: number;
  did?: string;
}

export async function apiGetOAuthURL(provider: string): Promise<{ authUrl: string; state: string; provider: string }> {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const res = await fetch(`${API_BASE}/auth/oauth/${provider}?redirect_uri=${encodeURIComponent(redirectUri)}`);
  if (!res.ok) throw new Error('Failed to get OAuth URL');
  const data = await res.json();
  return data.data ?? data;
}

export async function apiOAuthCallback(
  provider: string, code: string, state: string, redirectUri: string
): Promise<AuthResponse & { wallet?: WalletInfo }> {
  return authFetch(`/auth/oauth/${provider}/callback`, { code, state, redirectUri });
}

export async function apiGetWallet(token: string): Promise<WalletInfo | null> {
  const res = await fetch(`${API_BASE}/auth/wallet`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch wallet');
  const data = await res.json();
  return data.data ?? data;
}
